import { useState, useEffect, useCallback, useRef } from "react";
import { getRobotStatus } from "../services/robotApi";
import { CONFIG } from "../config";

/**
 * Custom hook for polling robot status
 * @param {string} deviceId - Device ID to poll
 * @param {boolean} enabled - Whether polling is enabled
 * @returns {{ robotData: import('../services/robotApi').RobotStatusData|null, isLoading: boolean, error: Error|null, refetch: Function }}
 */
export const useRobotStatus = (deviceId, enabled = true) => {
  const [robotData, setRobotData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    setRobotData(null);
    setError(null);
    setIsLoading(true);
  }, [deviceId]);

  const abortControllerRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    if (!deviceId) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    try {
      const data = await getRobotStatus(deviceId, signal);
      if (signal.aborted) return;
      setRobotData(data);
      setError(null);
      setIsLoading(false);
    } catch (err) {
      if (signal.aborted) return;
      console.error("Error fetching robot status:", err);
      setRobotData(null);
      setError(err);
      setIsLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    if (!enabled || !deviceId) {
      return;
    }

    // Initial fetch
    fetchStatus();

    // Set up polling interval
    intervalRef.current = setInterval(() => {
      fetchStatus();
    }, CONFIG.POLLING_INTERVAL);

    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [deviceId, enabled, fetchStatus]);

  return {
    robotData,
    isLoading,
    error,
    refetch: fetchStatus,
  };
};

/**
 * Get status color based on robot status
 * @param {string} status - Robot status
 * @returns {string} Color (green, orange, red, gray)
 */
export const getStatusColor = (status) => {
  switch (status) {
    case "STANDBY":
    case "CHARGING":
      return "green";
    case "MOVE":
    case "OPERATION":
      return "orange";
    case "EMERGENCY":
    case "ERROR":
      return "red";
    case "DISCONNECTED":
    case "OFFLINE":
    default:
      return "gray";
  }
};
